import { parent_routes } from "../../../router/all_routes";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import AcademicYearSelect from "../../../../core/common/AcademicYearSelect";
import { getAllEvaluationPeriodByLevel } from "../../../../api/generalParam";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getAllStudentNoteByEvaluationPeriod,
  getAverageByPeriod,
} from "../../../../api/notes";
import "./index.css";
import NoteDetailsModal from "./noteDetailsModal";
import { useChildrenDetails } from "../../../../core/common/context/ChildrenDetailsContext";
import { ExtendHeaderPageComponent } from "../parentDashBoard";
import NoRegistratedAlert from "../noRegistratedAlert";
import { Link } from "react-router";

const StudentNote = () => {
  const routes = parent_routes;

  const { childrenDetails, selectedChild, setSelectedChild } =
    useChildrenDetails();
  const [selectedChildInfos, setSelectedChildInfos] = useState<any>();

  // const userData = useSelector((state: any) => state.auth.user);
  const { selectedYear } = useAcademicYear();

  const [allEvaluationPeriodList, setAllEvaluationPeriodList] = useState<any[]>(
    [],
  );
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  const [notesByPeriod, setNotesByPeriod] = useState<Record<string, any>>({}); // ← stockage des notes par période

  const [averagesByPeriod, setAveragesByPeriod] = useState<Record<string, any>>(
    {},
  );
  const [showModal, setShowModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<{
    subject: string;
    values: Record<string, number>;
    evaluations: any[];
    average: number | null;
  } | null>(null);

  // charger les informations detwilles de l'enfant selectionner
  useEffect(() => {
    const matchedChild = childrenDetails.find(
      (c: any) => c.id === selectedChild,
    );
    if (matchedChild) setSelectedChildInfos(matchedChild);
  }, [selectedChild, childrenDetails]);

  // Chargement des périodes
  useEffect(() => {
    const yearId = selectedYear?.id;
    const levelId = selectedChildInfos?.class?.Level?.id;

    if (!yearId || !levelId) {
      setIsLoadingPeriods(false);
      setAllEvaluationPeriodList([]);
      setNotesByPeriod({});
      setAveragesByPeriod({});
      return;
    }

    let isActive = true;
    setIsLoadingPeriods(true);

    // reset immédiat à chaque changement d'année
    setAllEvaluationPeriodList([]);
    setNotesByPeriod({});
    setAveragesByPeriod({});

    const fetchPeriods = async () => {
      try {
        const response = await getAllEvaluationPeriodByLevel(levelId);
        const periods = response.data || [];

        if (!isActive) return;
        setAllEvaluationPeriodList(periods);
      } catch (error: any) {
        if (!isActive) return;
        toast.error(
          error.response?.data?.message || "Erreur de chargement des périodes",
        );
        setAllEvaluationPeriodList([]);
      } finally {
        if (!isActive) return;
        setIsLoadingPeriods(false);
      }
    };

    fetchPeriods();
    return () => {
      isActive = false;
    };
  }, [selectedYear?.id, selectedChildInfos?.class?.Level?.id]);

  // Fonction formatage
  function formatStudentNotes(notes: any[]) {
    const subjects: Record<string, any> = {};
    const evaluations: Record<string, any> = {};

    notes.forEach((n) => {
      const subjectId = n.id_subjectUE;
      const evalId = n.id_evaluationLevel;

      // Ajouter matière si absente
      if (!subjects[subjectId]) {
        subjects[subjectId] = {
          id: subjectId,
          subject: n.SubjectUE.Subject.label,
          values: {},
        };
      }

      // Ajouter type d’évaluation si absent
      if (!evaluations[evalId]) {
        evaluations[evalId] = {
          id: evalId,
          label: n.EvaluationLevel.Evaluation.label,
          abbreviation: n.EvaluationLevel.Evaluation.abbreviation,
        };
      }

      // Stocker la valeur
      subjects[subjectId].values[evalId] = n.value;
    });

    return {
      subjects: Object.values(subjects),
      evaluations: Object.values(evaluations),
    };
  }

  // Chargement des notes par période
  const fetchNotesByPeriod = async (periodId: string) => {
    try {
      const response = await getAllStudentNoteByEvaluationPeriod(
        selectedChildInfos?.id,
        periodId,
        selectedYear?.id,
      );
      const studentNotes = response.data || [];
      return formatStudentNotes(studentNotes);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Erreur de chargement des notes",
      );
      return { evaluations: [], subjects: [] };
    }
  };

  const fetchAverageByPeriod = async (periodId: string) => {
    try {
      const response = await getAverageByPeriod({
        id_studentProfil: selectedChildInfos?.id,
        id_evaluationPeriod: periodId,
        id_academicYear: selectedYear?.id,
        id_class: selectedChildInfos?.class?.id,
      });
      // console.log("average", response);
      const averages = response || {};
      return averages;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Erreur de chargement des moyennes",
      );
      return null;
    }
  };

  // Recharger les notes quand les périodes changent
  useEffect(() => {
    if (!selectedYear?.id) {
      setNotesByPeriod({});
      setAveragesByPeriod({});
      return;
    }

    // Si les notes sont bloquées pour l'étudiant, on n'appelle aucune API de notes/moyennes.
    if (selectedChildInfos?.blocked_Note) {
      setNotesByPeriod({});
      setAveragesByPeriod({});
      return;
    }

    if (allEvaluationPeriodList.length === 0) {
      setNotesByPeriod({});
      setAveragesByPeriod({});
      return;
    }

    let isActive = true;

    const loadData = async () => {
      const notesMap: Record<string, any> = {};
      const averagesMap: Record<string, any> = {};

      for (const period of allEvaluationPeriodList) {
        if (!isActive) return;

        // Si les notes de la période ne sont pas publiées, on n'appelle pas les APIs de notes.
        if (!period.publish_note) {
          notesMap[period.id] = {
            evaluations: [],
            subjects: [],
            unpublished: true,
          };
          averagesMap[period.id] = null;
          continue;
        }

        // Notes brutes
        notesMap[period.id] = await fetchNotesByPeriod(period.id);

        // Moyennes par matière
        const avg = await fetchAverageByPeriod(period.id);
        averagesMap[period.id] = avg;
      }

      if (!isActive) return;
      setNotesByPeriod(notesMap);
      setAveragesByPeriod(averagesMap);
    };

    loadData();
    return () => {
      isActive = false;
    };
  }, [
    allEvaluationPeriodList,
    selectedYear?.id,
    selectedChildInfos?.blocked_Note,
    selectedChildInfos?.class?.id,
  ]);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Notes</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  {" "}
                  <Link to={routes.parentDashboard}>Accueil</Link>
                </li>
                <li className="breadcrumb-item">
                  {selectedChildInfos?.class?.Level?.abbreviation}{" "}
                  {selectedChildInfos?.class?.label}
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Notes
                </li>
              </ol>
            </nav>
          </div>
          <ExtendHeaderPageComponent
            childrenDetails={childrenDetails}
            activeStudent={selectedChild}
            setSelectedChild={setSelectedChild}
            handleClick={(selectedChild: any) =>
              setSelectedChild(selectedChild)
            }
          />
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">
              {selectedChildInfos?.class?.Level?.abbreviation} -{" "}
              {selectedChildInfos?.class?.label}
            </h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <AcademicYearSelect className="me-2 d-block d-sm-inline-block" />
              </div>
            </div>
          </div>
          <div className="card-body">
            {selectedChildInfos?.blocked_Note ? (
              <div className="card mb-4 note-locked-card">
                <div className="note-locked-strip" />
                <div className="card-body text-center py-5">
                  <div className="mb-3 d-flex justify-content-center">
                    <div className="note-locked-icon-wrap">
                      <i className="ti ti-lock-access text-danger note-icon-lock" />
                    </div>
                  </div>

                  <h5 className="text-danger mb-2 fw-bold">
                    Accès aux notes bloqué
                  </h5>
                  <p className="text-muted mb-4 note-locked-text">
                    Vos notes sont actuellement{" "}
                    <strong className="text-danger">bloquées</strong>. L'accès
                    au relevé est indisponible pour votre profil.
                    <br />
                    Veuillez vous rapprocher de la scolarité pour régulariser
                    votre situation.
                  </p>

                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    <span className="badge bg-danger-subtle text-danger px-3 py-2 note-locked-pill">
                      <i className="ti ti-info-circle me-1" />
                      Contactez la scolarité
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="accordions-items-seperate" id="accordionExample">
                {allEvaluationPeriodList.length > 0 ? (
                  allEvaluationPeriodList.map((period: any, index) => {
                    const periodNotes = notesByPeriod[period.id];
                    if (!periodNotes) return null;

                    const isUnpublished = !period.publish_note;

                    return (
                      <div className="accordion-item" key={period.id}>
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse-${period.id}`}
                            aria-expanded="false"
                            aria-controls={`collapse-${period.id}`}
                          >
                            <span className="avatar avatar-sm bg-success me-2">
                              <i className="ti ti-checks" />
                            </span>
                            Notes du {period.label}
                          </button>
                        </h2>
                        <div
                          id={`collapse-${period.id}`}
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div
                            className="accordion-body"
                            style={{ paddingLeft: 0, paddingRight: 0 }}
                          >
                            {isUnpublished ? (
                              <div className="alert alert-info mb-0 mx-3">
                                Les notes de cette période ne sont pas encore
                                publiées.
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table custom-table">
                                  <thead className="thead-light">
                                    <tr>
                                      <th style={{ width: "70%" }}>Matière</th>
                                      <th
                                        style={{
                                          width: "30%",
                                          textAlign: "right",
                                        }}
                                      >
                                        Moyenne
                                      </th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {periodNotes.subjects.map(
                                      (sub: any, idx: number) => {
                                        const avgData = averagesByPeriod[
                                          period.id
                                        ]?.details
                                          ?.flatMap((d: any) =>
                                            d.ues.flatMap(
                                              (ue: any) => ue.subjects,
                                            ),
                                          )
                                          .find(
                                            (s: any) =>
                                              s.subjectLabel === sub.subject,
                                          );

                                        return (
                                          <tr
                                            key={idx}
                                            onClick={() => {
                                              setSelectedSubject({
                                                subject: sub.subject,
                                                values: sub.values,
                                                evaluations:
                                                  periodNotes.evaluations,
                                                average:
                                                  avgData?.average ?? null,
                                              });
                                              setShowModal(true);
                                            }}
                                          >
                                            {/* ===== Matière + notes ===== */}
                                            <td
                                              style={{
                                                maxWidth: "200px",
                                                paddingLeft: 7,
                                              }}
                                            >
                                              <div
                                                className="fw-semibold"
                                                style={{
                                                  whiteSpace: "normal",
                                                  overflowWrap: "break-word",
                                                  wordWrap: "break-word",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                {sub.subject}
                                              </div>

                                              {/* Notes par évaluation */}
                                              <div className="mt-1 text-muted fs-12">
                                                {periodNotes.evaluations
                                                  .filter(
                                                    (ev: any) =>
                                                      sub.values[ev.id] != null,
                                                  )
                                                  .map(
                                                    (
                                                      ev: any,
                                                      i: number,
                                                      arr: any[],
                                                    ) => (
                                                      <span key={ev.id}>
                                                        {Number(
                                                          sub.values[ev.id],
                                                        ).toFixed(2)}
                                                        {i < arr.length - 1 &&
                                                          " | "}
                                                      </span>
                                                    ),
                                                  )}
                                              </div>
                                            </td>

                                            {/* ===== Moyenne + badge ===== */}
                                            <td style={{ textAlign: "right" }}>
                                              <div className="fw-semibold">
                                                {avgData?.average?.toFixed(2) ??
                                                  "-"}
                                              </div>

                                              <div className="mt-1">
                                                <span
                                                  className={`badge fs-11 ${
                                                    avgData?.average >= 10
                                                      ? "badge-soft-success"
                                                      : "badge-soft-danger"
                                                  }`}
                                                >
                                                  {avgData?.average >= 10
                                                    ? "Validé"
                                                    : "Non validé"}
                                                </span>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      },
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : selectedChildInfos?.academicYear?.id !==
                  selectedYear?.id ? (
                  <NoRegistratedAlert />
                ) : (
                  // <p>Etudiant non inscript dans l'année sélectionner </p>
                  <p>Aucune période trouvée</p>
                )}
              </div>
            )}
          </div>
        </div>

        <NoteDetailsModal
          selectedSubject={selectedSubject}
          onClose={() => setShowModal(false)}
          show={showModal}
        />
      </div>
    </div>
  );
};

export default StudentNote;
